import React from 'react';
import PropTypes from 'prop-types';
import { LuHeartPulse, LuPencil, LuShieldCheck, LuStethoscope, LuTrash2, LuTriangleAlert, LuWallet } from 'react-icons/lu';
import DetailPane from '../../../../components/data/DetailPane';
import Badge from '../../../../components/ui/Badge';
import useInsuranceText from '../insuranceText';
import { INSURANCE_TYPES, endState, initialsOf, isExpiringSoon } from '../insuranceUtils';
import { BesBadge, useEndLabel } from './InsuranceCell';

const SECTION_TITLE = 'tw-px-2 tw-pb-1.5 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-[0.12em] tw-text-slate-400 dark:tw-text-slate-500';
const TYPE_ICONS = { accidentInsurance: LuShieldCheck, complementaryHealth: LuHeartPulse, malpractice: LuStethoscope };
const STATE_RING = {
  expired: 'tw-border-rose-400/50 tw-bg-rose-50/70 dark:tw-border-rose-400/30 dark:tw-bg-rose-500/[0.06]',
  soon: 'tw-border-amber-400/50 tw-bg-amber-50/70 dark:tw-border-amber-400/30 dark:tw-bg-amber-400/[0.06]',
  ok: 'tw-border-slate-200 tw-bg-white/70 dark:tw-border-white/10 dark:tw-bg-white/[0.03]',
  none: 'tw-border-slate-200 tw-bg-white/70 dark:tw-border-white/10 dark:tw-bg-white/[0.03]',
};

/**
 * The selected insurance record (replaces the old action bar): BES, the
 * three policies with their periods, time left and cost, the total, and
 * edit / delete. Docked beside the table on wide screens, a drawer otherwise.
 */
const InsuranceDetailPane = ({ docked, height, insurance, position, onPrev, onNext, onClose, onEdit, onDelete }) => {
  const { label, formatDate, formatMoney } = useInsuranceText();
  const endLabel = useEndLabel();
  const paneLabel = label('panel.label');

  if (!insurance) return <DetailPane docked={docked} height={height} open={false} label={paneLabel} onClose={onClose} />;

  return (
    <DetailPane
      docked={docked}
      height={height}
      open
      label={paneLabel}
      heading={label('panel.heading')}
      position={position}
      contentKey={insurance.id}
      identity={{
        avatar: initialsOf(insurance.employeeName),
        gradient: 'tw-from-rose-400 tw-to-pink-600 tw-shadow-rose-500/30',
        title: insurance.employeeName || '-',
        badges: (
          <>
            <BesBadge value={Boolean(insurance.hasBES)} />
            {isExpiringSoon(insurance) && (
              <Badge tone="warning">
                <LuTriangleAlert aria-hidden="true" className="tw-h-3 tw-w-3" />
                {label('toolbar.expiring')}
              </Badge>
            )}
          </>
        ),
      }}
      actions={[
        { key: 'edit', label: label('panel.edit'), icon: LuPencil, tint: 'blue', onClick: onEdit },
        { key: 'delete', label: label('panel.delete'), icon: LuTrash2, tint: 'rose', onClick: onDelete },
      ]}
      onPrev={onPrev}
      onNext={onNext}
      onClose={onClose}
    >
      <section aria-label={label('panel.policies')} className="tw-space-y-2 tw-px-1">
        <p className={SECTION_TITLE}>{label('panel.policies')}</p>
        {INSURANCE_TYPES.map((type) => {
          const policy = insurance[type] || {};
          const Icon = TYPE_ICONS[type];
          const state = endState(policy.endDate);
          const start = formatDate(policy.startDate);
          const end = formatDate(policy.endDate);
          const left = endLabel(policy.endDate);
          return (
            <div key={type} className={`tw-rounded-2xl tw-border tw-px-3.5 tw-py-3 ${STATE_RING[state]}`}>
              <p className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-semibold tw-text-slate-800 dark:tw-text-slate-100">
                <Icon aria-hidden="true" className="tw-h-4 tw-w-4 tw-text-rose-500" />
                <span className="tw-min-w-0 tw-flex-1 tw-truncate">{label(`types.${type}`)}</span>
                {left && <Badge tone={state === 'expired' ? 'danger' : 'warning'}>{left}</Badge>}
              </p>
              <p className="tw-mt-1.5 tw-text-[13px] tw-tabular-nums tw-text-slate-600 dark:tw-text-slate-300">
                {start || end ? `${start || '…'} – ${end || '…'}` : <span className="tw-italic tw-text-slate-400">{label('panel.noPeriod')}</span>}
              </p>
              {formatMoney(policy.cost) && (
                <p className="tw-mt-0.5 tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">
                  {label('panel.costPerPerson')}: <span className="tw-font-semibold tw-tabular-nums tw-text-slate-700 dark:tw-text-slate-200">{formatMoney(policy.cost)}</span>
                </p>
              )}
            </div>
          );
        })}
        <div className="tw-flex tw-items-center tw-gap-2.5 tw-rounded-2xl tw-bg-gradient-to-r tw-from-teal-500/10 tw-to-cyan-500/10 tw-px-3.5 tw-py-3">
          <LuWallet aria-hidden="true" className="tw-h-4 tw-w-4 tw-text-teal-600 dark:tw-text-teal-300" />
          <span className="tw-flex-1 tw-text-sm tw-font-medium tw-text-slate-600 dark:tw-text-slate-300">{label('panel.total')}</span>
          <span className="tw-text-base tw-font-semibold tw-tabular-nums tw-text-slate-900 dark:tw-text-white">{formatMoney(insurance.cost) || '-'}</span>
        </div>
      </section>
    </DetailPane>
  );
};

InsuranceDetailPane.propTypes = {
  docked: PropTypes.bool,
  height: PropTypes.number,
  insurance: PropTypes.object,
  position: PropTypes.shape({ index: PropTypes.number, total: PropTypes.number }),
  onPrev: PropTypes.func,
  onNext: PropTypes.func,
  onClose: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

export default InsuranceDetailPane;
